/**
 * AddServerDialog — one-shot onboarding wizard.
 *
 * The user supplies name + IP + SSH port + root password. The backend
 * /servers/onboard endpoint does the rest:
 *   - Installs the agent binary
 *   - Generates and stores all secrets
 *   - Registers the server in DB
 *   - Runs an inventory sync
 *
 * We render a live progress timeline as steps complete (or fail), so the
 * operator can see exactly where things went wrong if the bootstrap dies.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Plus, ServerCog, HardDrive, Lock } from "lucide-react";

import { ServersApi, type OnboardResult, type DiskInfo } from "@/lib/services";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STEP_LABELS_FA: Record<string, string> = {
  "lookup REST token": "بررسی توکن مخزن restic",
  "read panel SSH pubkey": "خواندن کلید SSH پنل",
  "fetch agent binary sha256": "دریافت چک‌سام باینری ایجنت",
  "SSH connect (password)": "اتصال SSH با پسورد روت",
  "deploy agent on target": "نصب ایجنت روی سرور",
  "resolve panel SSH key row": "یافتن رکورد کلید SSH",
  "register server in DB": "ثبت سرور در دیتابیس",
  "initial inventory sync": "همگام‌سازی اولیه اکانت‌ها",
};

function labelForStep(step: string): string {
  if (STEP_LABELS_FA[step]) return STEP_LABELS_FA[step];
  // Dynamic step "assign to diskN" → "تخصیص به diskN"
  const m = step.match(/^assign to (disk\d+)$/);
  if (m) return `تخصیص به ${m[1]}`;
  return step;
}

type Form = {
  name: string;
  sshHost: string;
  sshPort: string;
  rootPassword: string;
  agentPort: string;
  concurrencyMax: string;
  /** Empty string = "auto" (let backend pick least-loaded disk). */
  repoDisk: string;
};

const EMPTY: Form = {
  name: "",
  sshHost: "",
  sshPort: "22",
  rootPassword: "",
  agentPort: "9090",
  concurrencyMax: "2",
  repoDisk: "",
};

function fmtBytes(n: number): string {
  if (!n || n <= 0) return "0";
  // Persian unit labels for RTL consistency with the rest of the panel.
  const units = ["بایت", "کیلوبایت", "مگابایت", "گیگابایت", "ترابایت", "پتابایت"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export function AddServerDialog() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>(EMPTY);
  const [result, setResult] = useState<OnboardResult | null>(null);
  const qc = useQueryClient();

  // Load the disk pool so the operator can pick a target disk during
  // onboarding. `disks` is pool-only; `reserved` is listed alongside it so the
  // graveyard volume is visible but unselectable rather than mysteriously
  // absent from a box that plainly has it mounted.
  const { data: diskData } = useQuery({
    queryKey: ["disks"],
    queryFn: ServersApi.listDisksWithReserved,
    enabled: open,
    refetchInterval: open ? 30_000 : false,
  });
  const disks = diskData?.disks;
  const reservedDisks = diskData?.reserved ?? [];

  // A preview of what the BACKEND will choose, never an instruction to it.
  // This used to be sent as an explicit `repoDisk`, which meant "auto" bypassed
  // the server's pool filter entirely — and since a reserved disk is by
  // definition the emptiest one on the box, "most free space" picked the
  // graveyard volume every time.
  const autoPickedDisk = useMemo(() => {
    if (!disks || disks.length === 0) return null;
    return [...disks].sort((a, b) => b.freeBytes - a.freeBytes)[0]?.id ?? null;
  }, [disks]);

  const m = useMutation({
    mutationFn: () =>
      ServersApi.onboard({
        name: form.name.trim(),
        sshHost: form.sshHost.trim(),
        sshPort: Number(form.sshPort),
        rootPassword: form.rootPassword,
        agentPort: Number(form.agentPort) || undefined,
        concurrencyMax: Number(form.concurrencyMax) || undefined,
        // Empty = "auto": the backend decides, over pool disks only.
        repoDisk: form.repoDisk || undefined,
      }),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ["servers"] });
      if (r.ok) {
        toast.success(`سرور «${r.serverName}» اضافه شد`, {
          description: r.sync
            ? `${(r.sync.added + r.sync.updated).toLocaleString("en-US")} اکانت همگام‌سازی شد`
            : "همگام‌سازی اولیه ناموفق بود (قابل تکرار است).",
        });
      } else {
        toast.error("افزودن سرور ناموفق بود", {
          description: r.errorMessage ?? "جزئیات در پنجره را ببینید",
        });
      }
    },
    onError: (e: Error) => {
      toast.error("درخواست ناموفق بود", { description: e.message });
    },
  });

  const onClose = (next: boolean) => {
    if (m.isPending) return; // can't close mid-run
    setOpen(next);
    if (!next) {
      // give animation a tick before clearing so values don't flash empty
      setTimeout(() => {
        setForm(EMPTY);
        setResult(null);
        m.reset();
      }, 200);
    }
  };

  const canSubmit =
    form.name.match(/^[a-z][a-z0-9_-]*$/i) &&
    form.sshHost.trim().length > 0 &&
    Number(form.sshPort) > 0 &&
    form.rootPassword.length > 0 &&
    !m.isPending;

  return (
    <>
      <Button onClick={() => setOpen(true)} className="gap-1.5">
        <Plus className="size-4" /> افزودن سرور
      </Button>

      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          className="max-w-xl"
          // Block ESC and outside-click during pending run
          onEscapeKeyDown={(e) => m.isPending && e.preventDefault()}
          onPointerDownOutside={(e) => m.isPending && e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ServerCog className="size-5 text-primary" />
              افزودن سرور cPanel جدید
            </DialogTitle>
            <DialogDescription>
              نام سرور، IP و پسورد root را وارد کن؛ بقیه‌اش (نصب ایجنت، تولید
              کلیدها، همگام‌سازی) خودکار انجام می‌شود. پسورد فقط در همین جلسه
              استفاده می‌شود و هیچ‌جا ذخیره نمی‌شود.
            </DialogDescription>
          </DialogHeader>

          {!result ? (
            <FormFields
              form={form}
              disks={disks ?? []}
              reservedDisks={reservedDisks}
              autoPickedDisk={autoPickedDisk}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
              disabled={m.isPending}
            />
          ) : (
            <StepsTimeline result={result} pending={m.isPending} />
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            {!result ? (
              <>
                <Button variant="ghost" onClick={() => onClose(false)} disabled={m.isPending}>
                  انصراف
                </Button>
                <Button onClick={() => m.mutate()} disabled={!canSubmit}>
                  {m.isPending ? (
                    <>
                      <Loader2 className="size-4 animate-spin" /> در حال نصب…
                    </>
                  ) : (
                    "شروع نصب"
                  )}
                </Button>
              </>
            ) : (
              <>
                {!result.ok && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setResult(null);
                      m.reset();
                    }}
                  >
                    تلاش مجدد
                  </Button>
                )}
                <Button onClick={() => onClose(false)}>
                  {result.ok ? "تمام شد" : "بستن"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function FormFields({
  form,
  disks,
  reservedDisks,
  autoPickedDisk,
  onChange,
  disabled,
}: {
  form: Form;
  disks: DiskInfo[];
  reservedDisks: DiskInfo[];
  autoPickedDisk: string | null;
  onChange: (patch: Partial<Form>) => void;
  disabled: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 py-2">
      <div className="space-y-1 col-span-2">
        <Label htmlFor="srv-name">نام سرور (slug)</Label>
        <Input
          id="srv-name"
          dir="ltr"
          placeholder="cip5"
          className="lat-num"
          value={form.name}
          onChange={(e) => onChange({ name: e.target.value.toLowerCase() })}
          disabled={disabled}
        />
        <p className="text-[11px] text-muted-foreground">
          باید با یکی از نام‌های از پیش تعریف‌شده در <code className="lat-num">/etc/mfa-rest/htpasswd</code> یکی باشد (مثلاً <span className="lat-num">cip5</span>, <span className="lat-num">cip12</span>).
        </p>
      </div>

      <div className="space-y-1">
        <Label htmlFor="srv-host">IP یا hostname</Label>
        <Input
          id="srv-host"
          dir="ltr"
          placeholder="88.135.68.5"
          className="lat-num"
          value={form.sshHost}
          onChange={(e) => onChange({ sshHost: e.target.value })}
          disabled={disabled}
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor="srv-port">پورت SSH</Label>
        <Input
          id="srv-port"
          dir="ltr"
          inputMode="numeric"
          className="lat-num"
          value={form.sshPort}
          onChange={(e) => onChange({ sshPort: e.target.value.replace(/\D/g, "") })}
          disabled={disabled}
        />
      </div>

      <div className="space-y-1 col-span-2">
        <Label htmlFor="srv-pw">پسورد root</Label>
        <Input
          id="srv-pw"
          dir="ltr"
          type="password"
          className="lat-num"
          value={form.rootPassword}
          onChange={(e) => onChange({ rootPassword: e.target.value })}
          disabled={disabled}
          autoComplete="new-password"
        />
        <p className="text-[11px] text-muted-foreground">
          فقط برای نصب اولیه استفاده می‌شود؛ بعد از نصب، پنل از طریق کلید SSH متصل می‌شود.
        </p>
      </div>

      <div className="space-y-1">
        <Label htmlFor="srv-aport">پورت ایجنت (داخلی)</Label>
        <Input
          id="srv-aport"
          dir="ltr"
          inputMode="numeric"
          className="lat-num"
          value={form.agentPort}
          onChange={(e) => onChange({ agentPort: e.target.value.replace(/\D/g, "") })}
          disabled={disabled}
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor="srv-conc">حداکثر جاب همزمان</Label>
        <Input
          id="srv-conc"
          dir="ltr"
          inputMode="numeric"
          className="lat-num"
          value={form.concurrencyMax}
          onChange={(e) => onChange({ concurrencyMax: e.target.value.replace(/\D/g, "") })}
          disabled={disabled}
        />
      </div>

      <div className="space-y-1 col-span-2">
        <Label htmlFor="srv-disk">دیسک مقصد</Label>
        <Select
          value={form.repoDisk || "__auto__"}
          onValueChange={(v) => onChange({ repoDisk: v === "__auto__" ? "" : v })}
          disabled={disabled || disks.length === 0}
        >
          <SelectTrigger id="srv-disk" className="text-xs lat-num">
            <SelectValue placeholder="در حال بارگذاری…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__auto__" className="text-xs">
              <div className="flex items-center gap-2 whitespace-nowrap">
                <HardDrive className="size-3 shrink-0" />
                <span>خودکار</span>
                {autoPickedDisk && (
                  <span className="text-muted-foreground lat-num">
                    (پیشنهاد: {autoPickedDisk})
                  </span>
                )}
              </div>
            </SelectItem>
            {disks.map((d) => {
              const pct = d.totalBytes > 0 ? (d.usedBytes / d.totalBytes) * 100 : 0;
              return (
                <SelectItem key={d.id} value={d.id} className="text-xs">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <HardDrive className="size-3 shrink-0" />
                    <span className="lat-num font-medium">{d.id}</span>
                    <span className="text-muted-foreground lat-num">
                      {fmtBytes(d.freeBytes)} آزاد · {pct.toFixed(0)}٪ پر
                    </span>
                    <span className="text-muted-foreground">
                      · {d.serverNames.length.toLocaleString("en-US")} سرور
                    </span>
                  </div>
                </SelectItem>
              );
            })}
            {reservedDisks.map((d) => (
              <SelectItem key={d.id} value={d.id} disabled className="text-xs">
                <div className="flex items-center gap-2 whitespace-nowrap opacity-60">
                  <Lock className="size-3 shrink-0" />
                  <span className="lat-num font-medium">{d.id}</span>
                  <span className="text-muted-foreground">
                    رزرو‌شده — {d.reservedFor ?? "خارج از استخر restic"}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-muted-foreground">
          در حالت «خودکار»، سرور دیسکی با بیشترین فضای آزاد را از میان دیسک‌های
          استخر restic انتخاب می‌کند. دیسک‌های رزرو‌شده (مانند آرشیو گورستان) در
          این انتخاب نمی‌آیند و به‌صورت دستی هم پذیرفته نمی‌شوند.
        </p>
      </div>
    </div>
  );
}

function StepsTimeline({ result, pending }: { result: OnboardResult; pending: boolean }) {
  return (
    <div className="space-y-3 py-2">
      <ul className="space-y-1.5">
        {result.steps.map((s, i) => {
          const label = labelForStep(s.step);
          return (
            <li key={i} className="flex items-start gap-2 text-sm">
              {s.ok ? (
                <CheckCircle2 className="size-4 text-emerald-500 mt-0.5 shrink-0" />
              ) : (
                <XCircle className="size-4 text-red-500 mt-0.5 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="truncate">{label}</span>
                  <span className="text-[11px] text-muted-foreground lat-num">
                    {s.ms.toLocaleString("en-US")} ms
                  </span>
                </div>
                {!s.ok && s.detail && (
                  <pre dir="ltr" className="mt-1 text-[11px] bg-muted/50 rounded p-2 whitespace-pre-wrap break-all max-h-32 overflow-auto lat-num">
                    {s.detail}
                  </pre>
                )}
              </div>
            </li>
          );
        })}
        {pending && (
          <li className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> در حال اجرا…
          </li>
        )}
      </ul>

      {result.ok && result.sync && (
        <div className="text-xs text-muted-foreground bg-muted/40 rounded p-2">
          همگام‌سازی اولیه: {(result.sync.added + result.sync.updated).toLocaleString("en-US")} اکانت ثبت شد
          {result.sync.removed > 0 && ` — ${result.sync.removed.toLocaleString("en-US")} حذف`}.
        </div>
      )}

      {!result.ok && result.errorMessage && (
        <div className="text-xs text-red-600 dark:text-red-400 bg-red-500/10 rounded p-2">
          {result.errorMessage}
        </div>
      )}
    </div>
  );
}
